/**
 * Captures visitor attribution data (referrer, UTM parameters, session depth)
 * and persists in sessionStorage for the duration of the browsing session.
 *
 * Call initAttribution() in the App root to start tracking.
 * Call getAttribution() when creating an order to include attribution data.
 */

const STORAGE_KEY = 'autoqm_attribution';
const PAGE_COUNT_KEY = 'autoqm_page_count';
const SESSION_ID_KEY = 'autoqm_session_id';

// Resolve API base URL from the same origin to avoid hardcoding
const API_BASE = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * Generate a UUID v4-compatible random identifier.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a session ID stored in sessionStorage.
 */
function getOrCreateSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_ID_KEY);
  if (existing) return existing;
  const newId = generateUUID();
  sessionStorage.setItem(SESSION_ID_KEY, newId);
  return newId;
}

/**
 * Send a page view event to the backend for visitor analytics.
 * Uses navigator.sendBeacon for fire-and-forget; falls back to fetch with keepalive.
 * Admin pages (/admin/*) are excluded to avoid inflating visitor counts.
 */
export function sendPageView(path: string): void {
  // Skip tracking for admin pages
  if (path.startsWith('/admin')) return;

  const sessionId = getOrCreateSessionId();
  const deviceType = detectDevice();
  const referrer = document.referrer;

  const payload = JSON.stringify({
    session_id: sessionId,
    path,
    referrer,
    device_type: deviceType,
  });

  const url = `${API_BASE}/api/track/pageview`;

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  } else {
    // Fallback: fetch with keepalive so the request survives page unload
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Silently ignore tracking errors – must not affect main app flow
    });
  }
}

export interface Attribution {
  source_type: 'typein' | 'referral' | 'organic' | 'utm';
  utm_source: string;
  utm_medium?: string;
  utm_content?: string;
  utm_campaign?: string;
  device_type: string;
  referrer?: string;
  session_entry: string;
  session_start: string;
  session_pages: number;
  session_duration_sec: number;
}

/** Known search engine domains for organic detection */
const SEARCH_ENGINES = [
  'google.com', 'google.co.kr',
  'search.naver.com', 'naver.com',
  'bing.com', 'ntp.msn.com',
  'search.daum.net', 'daum.net',
  'yahoo.com',
];

function detectSourceType(referrer: string, hasUtm: boolean): Attribution['source_type'] {
  if (hasUtm) return 'utm';
  if (!referrer) return 'typein';

  try {
    const host = new URL(referrer).hostname;
    if (SEARCH_ENGINES.some((se) => host.includes(se))) return 'organic';
    return 'referral';
  } catch {
    return 'typein';
  }
}

function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return 'Mobile';
  if (/Tablet|iPad/i.test(ua)) return 'Tablet';
  return 'Desktop';
}

function extractUtmSource(referrer: string): string {
  if (!referrer) return '(direct)';

  try {
    const host = new URL(referrer).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return '(direct)';
  }
}

/**
 * Initialize attribution tracking. Call once at app mount.
 * Captures referrer/UTM on first page load. Tracks page views throughout session.
 */
export function initAttribution(): void {
  // Increment page count on every call (SPA navigation triggers App re-render)
  const currentCount = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '0', 10);
  sessionStorage.setItem(PAGE_COUNT_KEY, String(currentCount + 1));

  // Send server-side page view for the initial load
  sendPageView(window.location.pathname);

  // Only capture referrer/UTM on first visit (won't overwrite)
  if (sessionStorage.getItem(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmContent = params.get('utm_content');
  const utmCampaign = params.get('utm_campaign');
  const hasUtm = Boolean(utmSource);

  const referrer = document.referrer;
  const sourceType = detectSourceType(referrer, hasUtm);

  const data: Record<string, string> = {
    source_type: sourceType,
    utm_source: utmSource || extractUtmSource(referrer),
    device_type: detectDevice(),
    session_entry: window.location.pathname,
    session_start: new Date().toISOString(),
  };

  if (utmMedium) data.utm_medium = utmMedium;
  if (utmContent) data.utm_content = utmContent;
  if (utmCampaign) data.utm_campaign = utmCampaign;
  if (referrer) data.referrer = referrer;

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  sessionStorage.setItem(PAGE_COUNT_KEY, '1');
}

/**
 * Track a page navigation (call from router listener).
 * Also sends a server-side page view for analytics.
 */
export function trackPageView(): void {
  const current = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '0', 10);
  sessionStorage.setItem(PAGE_COUNT_KEY, String(current + 1));
  sendPageView(window.location.pathname);
}

/**
 * Get stored attribution data for order creation.
 * Computes session_pages and session_duration_sec at retrieval time.
 */
export function getAttribution(): Attribution | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const sessionPages = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '1', 10);
    const sessionStart = data.session_start ? new Date(data.session_start) : new Date();
    const durationSec = Math.round((Date.now() - sessionStart.getTime()) / 1000);

    return {
      ...data,
      session_pages: sessionPages,
      session_duration_sec: Math.max(0, durationSec),
    } as Attribution;
  } catch {
    return null;
  }
}
